import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import { getGeoLocation, getBrowserLocation, IBrowserLocation } from "~/util/providers/geoLocation";
import { _ } from "~/i18n";
import Scene from "~/scene";
import createSDSIcon from "~/ui/icon";
import { ICON_GEOLOCATION_24 } from "~/ui/icons";
import { locationDebugLog, locationDebugLocation } from "~/location-debug";
import { store } from "~/state/store";
import { setCoordinates } from "~/state/map/state";
import ControlPointer from "~/ui/controls/pointer";
import mapControls from "~/scene/control/mapcontrols";
import { getPointerDistance, getPointerCenterPx } from "~/util/pointer";
import { setLocation } from "~/state/user/state";
import WasmStoreMarker from "./wasm/store/wasm-store-marker";
import { WASM_ZINDEX } from "./wasm/const";

/**
 * @signal geoloc-open Otevreni spustene navigace
 * @signal geoloc-close Zavreni spustene navigace
 */
export class Geolocation {
	_drop: ControlPointer;

	_coords: any; // SceneCoords

	_layer: any; // SceneLayerMarker

	_geometry: any; // SceneGeometry

	_marker: any; // SceneMarkerMapy

	_watch: any;

	_dom: {
		button: HTMLButtonElement | HTMLMapyMapButtonElement;
		waiting: HTMLSpanElement;
	};

	_accuracy: number;

	_geolocating: boolean;

	_responseTimeout: number;

	_storeMarker: WasmStoreMarker;

	constructor() {
		if (!this.isSupported()) {
			return;
		}

		this._dom = {
			button: node("mapy-map-button", { title: _("geolocation.base") }),
			waiting: node("span", { innerHTML: _("geolocation.searching") }),
		};
		this._dom.button.appendChild(createSDSIcon({ symbol: ICON_GEOLOCATION_24 }));
		this._dom.button.addEventListener("click", this, false);

		this._layer = {};
		this._responseTimeout = null;
		this._geolocating = false;
		this._watch = null;

		this._coords = null;
		this._accuracy = Infinity;
		this._drop = null;

		this._layer.marker = Scene.createLayerMarker();
		this._marker = Scene.createMarkerGEO(Scene.coordsFromWGS84(15, 50));
		this._layer.marker.addMarker(this._marker);
		this._layer.geometry = Scene.createLayerGeometry();
		this._geometry = Scene.createGeometry(Scene.GEOMETRY.CIRCLE, null, null, { opacity: 0.2, color: "#00A8FF", outlineOpacity: 0 });
		this._layer.geometry.addGeometry(this._geometry);
		this._storeMarker = null;

		// poslouchani na udalosti z geo.js
		window.addEventListener("geoRefresh", this, false);
	}

	// eslint-disable-next-line class-methods-use-this
	isSupported() {
		return !!navigator.geolocation && !CONFIG.inFrame;
	}

	initDrop() {
		this._drop = new ControlPointer({
			type: "blue",
			snapHUDtoScreen: 25,
			showCaption: true,
			onClick: () => {
				const [longitude, latitude] = this._coords.toWGS84();

				store.dispatch(setCoordinates({
					longitude,
					latitude,
					animate: true,
				}));
				Mapy.getComponent("history").save();
			},
		});

		mapControls.addControlPointer(this._drop);
	}

	getButton() {
		return this._dom.button;
	}

	getPosition() {
		return {
			coords: this._coords,
			accuracy: this._accuracy,
		};
	}

	/**
	 * Ziskani polomeru [px] kruznice s presnosti.
	 * @return {Float}
	 */
	getRadius() {
		const pxs = this._geometry.getPixels();
		let output = 0;

		if (pxs.length) {
			output = Math.abs(pxs[1].x - pxs[0].x);
		}

		return output;
	}

	openLocation() {
		this.handleEvent();
	}

	handleEvent(event?) {
		if (CONFIG.inFrame) {
			return;
		}


		if (event?.type === "geoRefresh") {
			this._geolocation(true);
		// click
		} else {
			Mapy.getComponent("logger").log("geolocation-icon");

			if (Mapy.CUS.userweb?.grpcLocation || Mapy.CUS.userweb?.grpcLocationDebug) {
				this._getLocation();
			} else {
				this._geolocation();
			}
		}
	}

	grpcLoad() {
		if (!CONFIG.location) {
			locationDebugLog(`Moje poloha - nelze nastavit, protoze neprisla data z backendu`);

			return;
		}

		// pro zobrazeni modreho puntiku pouze fresh + precise
		if (CONFIG.location.dateFreshness === "fresh" && CONFIG.location.isPrecise) {
			locationDebugLog(`Nastavime moji polohu (presna, cerstva) i viewport`);
			this._geolocating = true;
			// nastavime
			this._responseOk({
				coords: {
					longitude: CONFIG.location.lon,
					latitude: CONFIG.location.lat,
					accuracy: CONFIG.location.precision,
					altitudeAccuracy: null,
					heading: null,
					speed: null,
				},
				timestamp: Date.now(),
			});
		} else {
			locationDebugLog(`Nelze nastavit moji polohu - cerstva: ${CONFIG.location.dateFreshness}, presna: ${CONFIG.location.isPrecise}`);
		}
	}

	_hide() {
		this._dom.button.classList.remove("done");
		this._dom.button.classList.remove("waiting");
		this._layer.marker.disable();
		this._layer.geometry.disable();
		Scene.removeLayer(this._layer.marker);
		Scene.removeLayer(this._layer.geometry);
		if (this._watch) {
			(window.geo || navigator.geolocation).clearWatch(this._watch);
			this._watch = null;
		}
		this._coords = null;
		this._accuracy = Infinity;

		if (this._storeMarker) {
			this._storeMarker.destroy();
		}

		this._storeMarker = null;

		// signal o zruseni navigace
		Scene.makeEvent("geoloc-close", null, this);

		// skryjeme kapicku
		this._drop.setPosition(null);
	}

	_geolocation(force = false) {
		this._dom.button.classList.remove("done");
		this._dom.button.classList.remove("waiting");
		this._dom.waiting.remove();

		if (this._geolocating && !force) {
			this._dom.button.classList.remove("waiting");
			this._dom.waiting.remove();
			this._geolocating = false;
			if (this._watch) {
				(window.geo || navigator.geolocation).clearWatch(this._watch);
				this._watch = null;
			}
			this._coords = null;
			this._accuracy = Infinity;
		} else if ((this._layer.marker.appended || this._storeMarker) && !force) { /* schovat */
			this._hide();
		} else {
			this._dom.button.classList.add("waiting");
			this._dom.button.appendChild(this._dom.waiting);

			if (Mapy.Util.isSmallResolution()) {
				this._watch = (window.geo || navigator.geolocation).watchPosition(this._responseOk.bind(this), this._responseError.bind(this), { timeout: 15000 });
			} else {
				getBrowserLocation().then(position => {
					if (position) {
						this._responseOk(position);
					} else {
						this._responseError();
					}
				});
			}
			this._geolocating = true;

			if (this._responseTimeout) {
				window.clearTimeout(this._responseTimeout);
			}

			// Firefox nevraci zadny callback na "nyni ne" https://bugzilla.mozilla.org/show_bug.cgi?id=675533
			this._responseTimeout = window.setTimeout(() => {
				this._responseError();
			}, 22000);
		}
	}

	_responseOk(position: IBrowserLocation) {
		if (this._responseTimeout) {
			window.clearTimeout(this._responseTimeout);
		}

		if (this._geolocating || this._watch) {
			this._dom.button.classList.remove("waiting");
			this._dom.button.classList.add("done");
			this._dom.waiting.remove();

			const coords = position.coords;
			const center = Scene.coordsFromWGS84(coords.longitude, coords.latitude);

			this._coords = center;
			this._accuracy = coords.accuracy;

			const radLat = coords.latitude * Math.PI / 180;
			const circle = 6371009 * 2 * Math.PI * Math.cos(radLat); /* v metrech */
			const frac = coords.accuracy / circle * 360;
			const c2 = Scene.coordsFromWGS84(coords.longitude + frac, coords.latitude);
			const cz = Scene.computeCenterZoom([center, c2], true);
			const centerMerc = center.toMercator();
			const edgeMerc = c2.toMercator();
			const mercDistance = Math.sqrt(Math.pow(edgeMerc[0] - centerMerc[0], 2) + Math.pow(edgeMerc[1] - centerMerc[1], 2)) >>> 0;

			cz[1] = Math.min(cz[1]-1, 18);
			if (this._geolocating) {
				const [longitude, latitude] = center.toWGS84();

				store.dispatch(setCoordinates({
					longitude,
					latitude,
					zoom: cz[1],
					animate: true,
				}));
				Mapy.getComponent("history").save();
			}

			this._storeMarker = new WasmStoreMarker({
				type: "geo-location",
				lon: coords.longitude,
				lat: coords.latitude,
				zIndex: WASM_ZINDEX.marker.geoLocation,
				customData: {
					radius: this._accuracy <= 1000
						? mercDistance
						: 0,
				},
			});

			this._geometry.setCoords([center, c2]);
			this._marker.setCoords(center);

			Scene.addLayer(this._layer.marker, true);
			Scene.addLayer(this._layer.geometry, true);
			this._layer.marker.enable();
			// kdyz je presnost do 1km, zobrazime kruh; jinak ne
			if (this._accuracy <= 1000) {
				this._layer.geometry.enable();
			}

			this._geolocating = false;

			const logLoc = {
				lat: position.coords.latitude,
				lon: position.coords.longitude,
				accuracy: this._accuracy,
			};

			Mapy.getComponent("logger").log("set-geolocation", { ...logLoc });

			store.dispatch(setLocation({
				source: "geolocation",
				latitude: position.coords.latitude,
				longitude: position.coords.longitude,
				accuracy: position.coords.accuracy,
				timestamp: position.timestamp,
			}));

			Scene.makeEvent("geoloc-open", null, this);

			// zobrazeni kapicky
			this._drop.setPosition(() => {
				const { longitude, latitude } = store.getState().map;

				return {
					position: getPointerCenterPx(center),
					caption: getPointerDistance(Scene.coordsFromWGS84(longitude, latitude), center),
				};
			});
		}
	}

	_responseError() {
		if (this._responseTimeout) {
			window.clearTimeout(this._responseTimeout);
		}
		if (this._geolocating || this._watch) {
			this._dom.button.classList.remove("waiting");
			this._dom.button.classList.remove("done");
			this._dom.waiting.remove();
			this._coords = null;
			this._accuracy = Infinity;
			this._geolocating = false;
		}
	}

	async _getLocation() {
		// pripojeno
		if (this._layer.marker.appended) {
			this._hide();
		} else if (Mapy.Util.isSmallResolution()) {
			locationDebugLog(`Moje poloha - responziv, jdeme po staru`);
			this._geolocation();
		} else {
			locationDebugLog(`Moje poloha - ziskame pozici z BE`);

			const locationResponse = await getGeoLocation({
				mobile: false,
			});
			const locationData = locationResponse.location;
			let browserData: IBrowserLocation = null;
			let useLocationData = false;
			let useBrowserLocation = false;

			if (
				locationData
				&& locationData.isPrecise
				&& (
					locationData.dateFreshness === "fresh"
					|| (locationData.dateFreshness === "old" && locationData.isTrustworthy)
				)
			) {
				useLocationData = true;
			} else {
				locationDebugLog(`Moje poloha - ziskame pozici z browseru`);
				// zkusime browser
				browserData = await getBrowserLocation();

				if (browserData) {
					useBrowserLocation = true;
				}
			}

			locationDebugLog(`Moje poloha - po ziskani dat`);

			if (!locationData && !browserData) {
				// chybova hlaska
				locationDebugLog(`Moje poloha - nelze pouzit zadny zdroj`);
			} else if (useLocationData) {
				this._geolocating = true;
				locationDebugLog(`Moje poloha - pouzije lokaci z BE`);
				locationDebugLocation(locationData);
				// nastavime
				this._responseOk({
					coords: {
						longitude: locationData.lon,
						latitude: locationData.lat,
						accuracy: locationData.precision,
						altitudeAccuracy: null,
						heading: null,
						speed: null,
					},
					timestamp: Date.now(),
				});
			} else if (useBrowserLocation) {
				locationDebugLog(`Moje poloha - pouzije lokaci z browseru`);
				locationDebugLocation(null, browserData);
				this._geolocating = true;
				// nastabime
				this._responseOk(browserData);
			}
		}
	}
}
